import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Tab, Tabs } from '@mui/material';
import { BORDER } from '../../OnlineJournal/components/analytics/chartKit';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { DocumentFilters, DocumentsMeta, ViewScope } from '../types';
import { analyticsApi } from './analyticsApi';
import { DEFAULT_ANALYTICS_FILTERS, listPatch, periodParams, resolvePeriod } from './analyticsFormat';
import { AnalyticsFilters, Period } from './analyticsTypes';
import AnalyticsKpi, { KpiView } from './AnalyticsKpi';
import AnalyticsToolbar from './AnalyticsToolbar';
import CleanupView from './CleanupView';
import HeatmapView from './HeatmapView';
import LeadtimeView from './LeadtimeView';
import PharmaciesView from './PharmaciesView';
import ProductsView from './ProductsView';
import TimelineView from './TimelineView';

type View = keyof DocumentsMeta['access']['analyticsTabs'];

const VIEWS: Array<{ value: View; label: string }> = [
  { value: 'timeline', label: 'Динамика' },
  { value: 'leadtime', label: 'Сроки обработки' },
  { value: 'pharmacies', label: 'Аптеки' },
  { value: 'heatmap', label: 'Активность по часам' },
  { value: 'cleanup', label: 'Автоочистка' },
  { value: 'products', label: 'Товар' }
];

const SCOPE_LABELS: Record<ViewScope, string> = {
  all: 'вся сеть',
  assigned_pharmacies: 'курируемые аптеки',
  own_region: 'свой регион'
};

/** Пауза перед запросом после правки фильтров (ввод дат, выбор нескольких аптек подряд) */
const DEBOUNCE_MS = 300;

interface Props {
  meta: DocumentsMeta;
  /** Вкладка «Аналитика» открыта: на других вкладках запросов нет */
  active: boolean;
  reloadToken: number;
  /** Открыть список документов с фильтрами (переход из показателя или графика) */
  onOpenList: (patch: Partial<DocumentFilters>) => void;
}

/**
 * Аналитика документов: фильтры, ключевые показатели и подвкладки.
 * Подвкладка загружается при первом открытии и остаётся смонтированной: при возврате без смены фильтров
 * данные не перезапрашиваются, скрытая подвкладка запросов не шлёт.
 */
const AnalyticsPanel: React.FC<Props> = ({ meta, active, reloadToken, onOpenList }) => {
  const { access } = meta;
  // подвкладка без права (documents.invoices.analytics_<tab>) не рендерится
  const views = useMemo(() => VIEWS.filter(v => Boolean(access.analyticsTabs[v.value])), [access.analyticsTabs]);
  const allowed = (v: View) => Boolean(access.analyticsTabs[v]);
  const [filters, setFilters] = useState<AnalyticsFilters>(DEFAULT_ANALYTICS_FILTERS);
  const [applied, setApplied] = useState<AnalyticsFilters>(DEFAULT_ANALYTICS_FILTERS);
  const [view, setView] = useState<View>(() => (views.length ? views[0].value : 'timeline'));
  const [visited, setVisited] = useState<View[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setApplied(filters), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    setVisited(prev => (prev.indexOf(view) === -1 ? [...prev, view] : prev));
  }, [view]);

  const patch = useCallback((p: Partial<AnalyticsFilters>) => setFilters(f => ({ ...f, ...p })), []);

  // пресет считается от «сегодня» при каждом рендере: после полуночи «Обновить» сдвигает период
  const draft = resolvePeriod(filters);
  const resolved = resolvePeriod(applied);
  const { from, to } = resolved;
  const periodError = resolved.error;
  const period = useMemo<Period>(() => ({ from, to }), [from, to]);
  const paramsKey = JSON.stringify(periodParams(applied, period));
  const params = useMemo<Record<string, string>>(() => JSON.parse(paramsKey), [paramsKey]);
  const enabled = active && !periodError;

  const kpi = useLoad(
    enabled ? (silent: boolean, signal: AbortSignal) => analyticsApi.kpi(params, silent, signal) : null,
    `kpi|${paramsKey}`, reloadToken, 'Не удалось рассчитать показатели документов'
  );

  const onCohort = useCallback((extra: Partial<DocumentFilters>, range?: Period) => {
    onOpenList(listPatch(applied, range || period, extra));
  }, [applied, period, onOpenList]);

  // «На контроле» - текущее состояние: те же фильтры, но без периода
  const onWarnings = useCallback((extra: Partial<DocumentFilters> = {}) => {
    onOpenList({
      type: applied.type, route: applied.route, marking: applied.marking, pharmacy: applied.pharmacy, region: applied.region, warn: true, ...extra
    });
  }, [applied, onOpenList]);

  const tabs = access.analyticsTabs;
  const openView = useCallback((v: KpiView) => { if (tabs[v]) setView(v); }, [tabs]);

  const narrowed = access.analyticsScope !== null && access.analyticsScope !== 'all' && access.analyticsScope !== access.viewScope;
  const mounted = (v: View) => allowed(v) && (v === view || visited.indexOf(v) !== -1);
  const pane = (v: View) => ({ display: v === view ? 'block' : 'none' });
  const viewProps = (v: View) => ({ params, paramsKey, period, active: enabled && v === view, reloadToken, onCohort });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <AnalyticsToolbar meta={meta} filters={filters} period={draft} onChange={patch} />

      {narrowed && access.analyticsScope && (
        <Alert severity="info" icon={false} sx={{ py: 0, fontSize: '0.78rem' }}>
          Аналитика строится по вашей зоне аналитики ({SCOPE_LABELS[access.analyticsScope]}), поэтому список документов может показать больше.
        </Alert>
      )}

      {draft.error && <Alert severity="warning">{draft.error}</Alert>}

      <Box sx={{ display: draft.error || periodError ? 'none' : 'flex', flexDirection: 'column', gap: 1.5 }}>
        <AnalyticsKpi
          data={kpi.data} loading={kpi.loading} error={kpi.error ? kpi.error.text : null} onRetry={kpi.retry}
          onCohort={onCohort} onWarnings={onWarnings} onView={openView}
        />

        {views.length > 0 && (
          <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
            <Tabs
              value={view} onChange={(_, v: View) => setView(v)} variant="scrollable" scrollButtons="auto"
              sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, py: 0.5, px: 1.75, textTransform: 'none', fontSize: '0.84rem', fontWeight: 600 } }}
            >
              {views.map(v => <Tab key={v.value} value={v.value} label={v.label} />)}
            </Tabs>
          </Box>
        )}

        {mounted('timeline') && <Box sx={pane('timeline')}><TimelineView {...viewProps('timeline')} days={resolved.days} /></Box>}
        {mounted('leadtime') && <Box sx={pane('leadtime')}><LeadtimeView {...viewProps('leadtime')} /></Box>}
        {mounted('pharmacies') && <Box sx={pane('pharmacies')}><PharmaciesView {...viewProps('pharmacies')} /></Box>}
        {mounted('heatmap') && <Box sx={pane('heatmap')}><HeatmapView {...viewProps('heatmap')} /></Box>}
        {mounted('cleanup') && <Box sx={pane('cleanup')}><CleanupView {...viewProps('cleanup')} days={resolved.days} onWarnings={onWarnings} /></Box>}
        {mounted('products') && <Box sx={pane('products')}><ProductsView {...viewProps('products')} /></Box>}
      </Box>
    </Box>
  );
};

export default React.memo(AnalyticsPanel);
